export type Point = { x: number; y: number };
const cross = (a: Point, b: Point, c: Point) => (b.x-a.x)*(c.y-a.y)-(b.y-a.y)*(c.x-a.x);
const onSegment = (a: Point,b: Point,p: Point) => Math.abs(cross(a,b,p)) < 1e-8 && p.x >= Math.min(a.x,b.x)-1e-8 && p.x <= Math.max(a.x,b.x)+1e-8 && p.y >= Math.min(a.y,b.y)-1e-8 && p.y <= Math.max(a.y,b.y)+1e-8;
export function polygonError(points: Point[]): string {
  if (!Array.isArray(points) || points.length < 3 || points.length > 40) return 'Use 3–40 points to define a closed outline.';
  if (points.some(p => !p || !Number.isFinite(p.x) || !Number.isFinite(p.y) || p.x < 0 || p.x > 1 || p.y < 0 || p.y > 1)) return 'Keep every point inside the drawing area.';
  if (points.some((p,i) => points.some((q,j) => i !== j && Math.hypot(p.x-q.x,p.y-q.y) < .005))) return 'Two points are too close together. Move or remove one.';
  for (let i=0;i<points.length;i++) for (let j=i+1;j<points.length;j++) {
    if (j === i+1 || (i===0 && j===points.length-1)) continue;
    const a=points[i]!, b=points[(i+1)%points.length]!, c=points[j]!, d=points[(j+1)%points.length]!;
    if ((cross(a,b,c)*cross(a,b,d)<0 && cross(c,d,a)*cross(c,d,b)<0) || onSegment(a,b,c) || onSegment(a,b,d) || onSegment(c,d,a) || onSegment(c,d,b)) return 'The outline crosses itself. Move a point so the edges do not intersect.';
  }
  const area = Math.abs(points.reduce((sum,p,i) => {const q=points[(i+1)%points.length]!; return sum+p.x*q.y-q.x*p.y;},0))/2;
  if (area < .01) return 'The outline is too narrow. Spread the points farther apart.';
  return '';
}
export function normalizePolygon(points: Point[]): Point[] {
  const minX=Math.min(...points.map(p=>p.x)), minY=Math.min(...points.map(p=>p.y));
  const width=Math.max(...points.map(p=>p.x))-minX, height=Math.max(...points.map(p=>p.y))-minY;
  return points.map(p=>({x: (p.x-minX)/(width || 1), y: (p.y-minY)/(height || 1)}));
}
export function containsPoint(points: Point[], point: Point): boolean {
  let inside=false;
  for (let i=0,j=points.length-1;i<points.length;j=i++) {
    const a=points[i]!,b=points[j]!;
    if ((a.y>point.y)!==(b.y>point.y) && point.x < (b.x-a.x)*(point.y-a.y)/(b.y-a.y)+a.x) inside=!inside;
  }
  return inside;
}
// Pick a readable interior label position, including for concave outlines.
export function polygonLabel(points: Point[]): Point {
  let best={x:.5,y:.5}, distance=-1;
  for(let y=.025;y<1;y+=.05) for(let x=.025;x<1;x+=.05) {
    const p={x,y}; if (!containsPoint(points,p)) continue;
    const nearest=Math.min(...points.map((a,i)=>{const b=points[(i+1)%points.length]!; const dx=b.x-a.x,dy=b.y-a.y;const t=Math.max(0,Math.min(1,((x-a.x)*dx+(y-a.y)*dy)/(dx*dx+dy*dy || 1)));return Math.hypot(x-a.x-t*dx,y-a.y-t*dy);}));
    if(nearest>distance){distance=nearest;best=p;}
  }
  return best;
}
